import {
	Component,
	Element,
	Event,
	EventEmitter,
	h,
	Method,
	Prop,
} from '@stencil/core';
import { calculateChildTagName } from '../_helpers/prefixes';
import { InputComponentInterface } from '../_interfaces/input-component.interface';
import { TextInputAppearances, TextInputState } from './text-input.controls';

export interface ClearButtonPressEvent {
	name: string;
	oldValue: string;
	clearButtonText: string;
}

@Component({
	tag: 'ds-text-input',
})
export class TextInput implements InputComponentInterface {
	@Prop({ reflect: true })
	appearance: TextInputAppearances = 'primary';
	@Prop()
	name?: string;
	@Prop()
	value?: string = '';
	@Prop()
	label?: string;
	@Prop()
	helperText?: string;
	@Prop()
	isRequired?: boolean;
	@Prop()
	optionalTag?: string;
	@Prop()
	placeholder?: string;
	@Prop()
	state?: TextInputState = '';
	@Prop()
	errorMessage?: string;
	@Prop()
	compact?: boolean;
	@Prop()
	clearButtonText?: string;
	@Prop()
	showClearButton? = false;

	@Element() el: HTMLDsTextInputElement;
	inputEl: HTMLInputElement;
	buttonEl: HTMLDsButtonElement;

	@Method()
	async setValue(newVal: string) {
		this.value = newVal;
	}

	@Method()
	async setFocus() {
		this.inputEl.focus();
	}

	@Event() clearButtonPressed: EventEmitter<ClearButtonPressEvent>;

	handleInput(e): void {
		this.value = e.value;

		if (this.buttonEl) {
			const hiddenClass = 'ds-hidden';
			this.value === ''
				? this.buttonEl.classList.add(hiddenClass)
				: this.buttonEl.classList.remove(hiddenClass);
		}
	}

	handleChange(): void {
		if (this.state === 'prefilled') {
			this.inputEl.classList.remove('ds-prefilled');
		}
	}

	clearTextInputValue(event): void {
		event.preventDefault();
		event.stopPropagation();
		this.clearButtonPressed.emit({
			oldValue: this.value,
			name: this.name,
			clearButtonText: this.clearButtonText,
		});

		this.setValue('');
		this.setFocus();
	}

	// Set aria-expanded to false when Escape is pressed
	handleKeyDown(event: KeyboardEvent): void {
		if (event.key === 'Escape' && this.inputEl) {
			this.inputEl.setAttribute('aria-expanded', 'false');
		}
	}

	render() {
		const Button = calculateChildTagName(this.el, 'ds-button');
		return (
			<div class="ds-text-input-container">
				<div class="ds-label">
					{this.label ? (
						<label
							id="ds-label-id"
							class={this.compact ? 'ds-eyebrow-s' : 'ds-eyebrow-m'}
							htmlFor={this.name ? 'ds-input-' + this.name : ''}
						>
							{this.label}
						</label>
					) : (
						''
					)}
					{!this.isRequired && this.optionalTag ? (
						<span
							class={this.compact ? 'ds-body-xs-compact' : 'ds-body-s-compact'}
						>
							{this.optionalTag}
						</span>
					) : (
						false
					)}
				</div>
				{this.helperText ? (
					<div
						id="ds-helper-text-id"
						class={`${
							this.compact ? 'ds-body-xs' : 'ds-body-s'
						} ds-helper-text`}
					>
						{this.helperText}
					</div>
				) : (
					false
				)}
				{this.state === 'error' && this.errorMessage ? (
					<p class="ds-body-s ds-error-label">
						<i class="fas fa-exclamation-triangle"></i>
						{this.errorMessage}
					</p>
				) : (
					false
				)}
				<div class="ds-input-wrapper">
					<input
						class={
							this.state === 'prefilled'
								? 'ds-body-m ds-prefilled'
								: 'ds-body-m'
						}
						type="text"
						id={this.name ? 'ds-input-' + this.name : ''}
						name={this.name}
						value={this.value}
						placeholder={this.placeholder}
						disabled={this.state === 'disabled' ? true : false}
						aria-expanded={this.value.trim() !== '' ? 'true' : 'false'}
						aria-describedby="ds-helper-text-id"
						role="combobox"
						autocomplete="off"
						onFocus={() => {
							if (this.inputEl) {
								this.inputEl.setAttribute('aria-expanded', 'true');
							}
						}}
						onBlur={() => {
							if (this.inputEl) {
								this.inputEl.setAttribute('aria-expanded', 'false');
							}
						}}
						onKeyDown={(e) => this.handleKeyDown(e)}
						onChange={this.handleChange}
						onInput={(e) => this.handleInput(e.target)}
						ref={(el) => (this.inputEl = el)}
					/>
					{this.showClearButton && this.clearButtonText && this.value ? (
						<Button
							class="ds-button ds-clear-button"
							type="tertiary"
							compact
							accessibility-role="button"
							accessibility-label={this.clearButtonText}
							onClick={(e) => this.clearTextInputValue(e)}
							onKeydown={(e) => e.stopPropagation()}
							ref={(el) => (this.buttonEl = el)}
						>
							<span class="ds-clear-button-text">{this.clearButtonText}</span>
							<i class="fas fa-trash" aria-hidden="true"></i>
						</Button>
					) : (
						false
					)}
					{this.state == 'validated' ? (
						<i class="fas fa-check-circle" />
					) : (
						false
					)}
				</div>
			</div>
		);
	}
}
